import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useLocation, useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialGroupLaboratoryActions from "../../../actions/trialGroupLaboratory";
import * as TrialGroupLaboratoryContractActions from "../../../actions/trialGroupLaboratoryContract";
import { NotificationAlert } from "../../../components/common/index";
import {
    DatePickerFormInputFabric,
    TextFormInput,
} from "../../../components/form/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { isUrl } from "../../../functions/string";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    GroupTrialLaboratoryContractCreateBreadcrumbs,
    GroupTrialLaboratoryContractDetailsBreadcrumbs,
    TrialGroupLaboratoryContractCreateBreadcrumbs,
    TrialGroupLaboratoryContractDetailsBreadcrumbs,
} from "../../../helpers/breadcrumbHelper";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    GroupTrialLaboratoryContractSubMenu,
    TrialGroupLaboratoryContractSubMenu,
} from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { ITrialGroupLaboratoryContractReduxFormState } from "../../../reducers/reactReduxForms/trialGroupLaboratoryContract";

import "./TrialGroupLaboratoryContractPage.scss";

interface TrialGroupLaboratoryContractPageProps {
    form: Dtos.TrialGroupLaboratoryContract & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: ITrialGroupLaboratoryContractReduxFormState;
    trialGroupLaboratoryContract: Dtos.TrialGroupLaboratoryContract;
    lookups: Dtos.Lookup[];
    loadingTrialGroupLaboratoryContract: boolean;
    loadTrialGroupLaboratoryContractSuccess: boolean;
    loadTrialGroupLaboratoryContractFailure: boolean;
    loadTrialGroupLaboratoryContractErrorCode: string;
    savingTrialGroupLaboratoryContract: boolean;
    saveTrialGroupLaboratoryContractSuccess: boolean;
    saveTrialGroupLaboratoryContractFailure: boolean;
    creatingTrialGroupLaboratoryContract: boolean;
    createTrialGroupLaboratoryContractSuccess: boolean;
    createTrialGroupLaboratoryContractFailure: boolean;
    validatingTrialGroupLaboratoryContract: boolean;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    trialGroupLaboratory: Dtos.TrialGroupLaboratory;
    loadingTrialGroupLaboratory: boolean;
    loadTrialGroupLaboratorySuccess: boolean;
    loadTrialGroupLaboratoryFailure: boolean;
    loadTrialGroupLaboratoryErrorCode: string;
    clearTrialGroupLaboratoryContract: TrialGroupLaboratoryContractActions.ITrialGroupLaboratoryContractClearActionCreator;
    loadTrialGroupLaboratoryContract: TrialGroupLaboratoryContractActions.ITrialGroupLaboratoryContractLoadByIdActionCreator;
    saveTrialGroupLaboratoryContract: TrialGroupLaboratoryContractActions.ITrialGroupLaboratoryContractSaveActionCreator;
    createTrialGroupLaboratoryContract: TrialGroupLaboratoryContractActions.ITrialGroupLaboratoryContractCreateActionCreator;
    validateTrialGroupLaboratoryContract: TrialGroupLaboratoryContractActions.ITrialGroupLaboratoryContractValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    loadTrialGroupLaboratory: TrialGroupLaboratoryActions.ITrialGroupLaboratoryLoadByIdActionCreator;
    clearTrialGroupLaboratory: TrialGroupLaboratoryActions.ITrialGroupLaboratoryClearActionCreator;
}

const reduxFormName: string = "reduxForms.trialGroupLaboratoryContract";

const TrialGroupLaboratoryContractPage: FC<TrialGroupLaboratoryContractPageProps> = ({
    form,
    formState,
    formProperties,
    trialGroupLaboratoryContract,
    lookups,
    loadingTrialGroupLaboratoryContract,
    loadTrialGroupLaboratoryContractFailure,
    loadTrialGroupLaboratoryContractErrorCode,
    savingTrialGroupLaboratoryContract,
    saveTrialGroupLaboratoryContractSuccess,
    saveTrialGroupLaboratoryContractFailure,
    creatingTrialGroupLaboratoryContract,
    validatingTrialGroupLaboratoryContract,
    validationFailures,
    permissions,
    trialGroupLaboratory,
    loadingTrialGroupLaboratory,
    loadTrialGroupLaboratoryFailure,
    loadTrialGroupLaboratoryErrorCode,
    clearTrialGroupLaboratoryContract,
    loadTrialGroupLaboratoryContract,
    saveTrialGroupLaboratoryContract,
    createTrialGroupLaboratoryContract,
    validateTrialGroupLaboratoryContract,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems,
    loadTrialGroupLaboratory,
    clearTrialGroupLaboratory
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { groupId } = useParams<{ groupId: string }>();
    const { trialGroupId } = useParams<{ trialGroupId: string }>();
    const { trialGroupLaboratoryId } = useParams<{trialGroupLaboratoryId: string;}>();
    const { trialGroupLaboratoryContractId } = useParams<{trialGroupLaboratoryContractId: string;}>();
    const trialIdNumber = parseInt(trialId, 10);
    const groupIdNumber = parseInt(groupId, 10);
    const trialGroupIdNumber = parseInt(trialGroupId, 10);
    const trialGroupLaboratoryIdNumber = parseInt(trialGroupLaboratoryId, 10);
    const trialGroupLaboratoryContractIdNumber = parseInt(trialGroupLaboratoryContractId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();
    const location = useLocation();

    const loadReduxForm = () => {
        let extraData: Dtos.TrialGroupLaboratoryContract = {} as Dtos.TrialGroupLaboratoryContract;

        loadForm(
            reduxFormName,
            Object.assign({ ...trialGroupLaboratoryContract }, extraData)
        );
    };


    // Load Trial Group Laboratory & Contract
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (
            trialIdNumber &&
            groupIdNumber &&
            trialGroupIdNumber &&
            trialGroupLaboratoryIdNumber &&
            trialGroupLaboratoryContractIdNumber &&
            !trialGroupLaboratoryContract &&
            !loadingTrialGroupLaboratoryContract
        ) {
            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoryContractDetailsBreadcrumbs
                    : GroupTrialLaboratoryContractDetailsBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroupLaboratoryIdNumber,
                    trialGroupLaboratoryContractIdNumber
                )
            );

            loadTrialGroupLaboratoryContract(
                trialGroupLaboratoryContractIdNumber
            );
        } else if (
            trialIdNumber &&
            groupIdNumber &&
            trialGroupIdNumber &&
            trialGroupLaboratoryIdNumber &&
            !trialGroupLaboratoryContractIdNumber &&
            !trialGroupLaboratoryContract &&
            !creatingTrialGroupLaboratoryContract
        ) {
            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoryContractCreateBreadcrumbs
                    : GroupTrialLaboratoryContractCreateBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroupLaboratoryIdNumber
                )
            );
            setDrawerItems([]);
            createTrialGroupLaboratoryContract(trialGroupLaboratoryIdNumber);
        } else if (trialGroupLaboratoryContract && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }

        if (
            trialGroupLaboratoryIdNumber &&
            !trialGroupLaboratory &&
            !loadingTrialGroupLaboratory
        ) {
            loadTrialGroupLaboratory(trialGroupLaboratoryIdNumber);
        }
    }, [
        location,
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroupLaboratoryIdNumber,
        trialGroupLaboratoryContractIdNumber,
        trialGroupLaboratoryContract,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        loadTrialGroupLaboratoryContract,
        trialGroupLaboratory,
        loadTrialGroupLaboratory,
        setFormInitalised,
        TrialGroupLaboratoryContractDetailsBreadcrumbs,
        GroupTrialLaboratoryContractDetailsBreadcrumbs,
        TrialGroupLaboratoryContractCreateBreadcrumbs,
        GroupTrialLaboratoryContractCreateBreadcrumbs,
        createTrialGroupLaboratoryContract,
        loadReduxForm,
        loadingTrialGroupLaboratoryContract,
        creatingTrialGroupLaboratoryContract,
        loadingTrialGroupLaboratory,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialGroupLaboratoryContractFailure) {
            navigate(`/error/${loadTrialGroupLaboratoryContractErrorCode}`);
        }
        if (loadTrialGroupLaboratoryFailure) {
            navigate(`/error/${loadTrialGroupLaboratoryErrorCode}`);
        }
    }, [
        loadTrialGroupLaboratoryContractFailure,
        loadTrialGroupLaboratoryFailure,
        loadTrialGroupLaboratoryContractErrorCode,
        loadTrialGroupLaboratoryErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (
            trialIdNumber &&
            groupIdNumber &&
            trialGroupIdNumber &&
            trialGroupLaboratoryIdNumber &&
            trialGroupLaboratoryContractIdNumber &&
            trialGroupLaboratoryContract &&
            trialGroupLaboratory
        ) {
            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoryContractDetailsBreadcrumbs
                    : GroupTrialLaboratoryContractDetailsBreadcrumbs;

            const drawerItemSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoryContractSubMenu
                    : GroupTrialLaboratoryContractSubMenu;

            setDrawerItems(
                drawerItemSetter(trialGroupLaboratoryContract, permissions)
            );
            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroupLaboratoryIdNumber,
                    trialGroupLaboratoryContractIdNumber,
                    trialGroupLaboratory?.trialGroup?.trial?.displayName,
                    trialGroupLaboratory?.trialGroup?.group?.name,
                    trialGroupLaboratory?.laboratory?.name,
                    trialGroupLaboratoryContract?.name
                )
            );
        } else if (
            trialIdNumber &&
            trialGroupLaboratoryIdNumber &&
            !trialGroupLaboratoryContractIdNumber &&
            trialGroupLaboratory
        ) {
            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoryContractCreateBreadcrumbs
                    : GroupTrialLaboratoryContractCreateBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroupLaboratoryIdNumber,
                    trialGroupLaboratory?.trialGroup?.trial?.displayName,
                    trialGroupLaboratory?.trialGroup?.group?.name,
                    trialGroupLaboratory?.laboratory?.name
                )
            );
        }
    }, [
        location,
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroupLaboratoryIdNumber,
        trialGroupLaboratoryContractIdNumber,
        trialGroupLaboratoryContract,
        trialGroupLaboratory,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        TrialGroupLaboratoryContractDetailsBreadcrumbs,
        GroupTrialLaboratoryContractDetailsBreadcrumbs,
        TrialGroupLaboratoryContractCreateBreadcrumbs,
        GroupTrialLaboratoryContractCreateBreadcrumbs,
        TrialGroupLaboratoryContractSubMenu,
        GroupTrialLaboratoryContractSubMenu,
    ]);

    // Reload trial group laboratory contract after saving
    useEffect(() => {
        if (
            !savingTrialGroupLaboratoryContract &&
            saveTrialGroupLaboratoryContractSuccess
        ) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial Group Laboratory Contract saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            const baseRoute = location?.pathname?.split("/")[1];

            if (saveAndReturn) {
                const url =
                    baseRoute === "trials"
                        ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryIdNumber}/contracts`
                        : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryIdNumber}/contracts`;

                navigate(url);
            } else if (!trialGroupLaboratoryContractIdNumber) {
                const url =
                    baseRoute === "trials"
                        ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryIdNumber}/contracts/${trialGroupLaboratoryContract?.id}/details`
                        : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryIdNumber}/contracts/${trialGroupLaboratoryContract?.id}/details`;
                navigate(url);
                clearTrialGroupLaboratoryContract();
            } else {
                loadTrialGroupLaboratoryContract(
                    trialGroupLaboratoryContractIdNumber
                );
            }
        }
    }, [
        savingTrialGroupLaboratoryContract,
        saveTrialGroupLaboratoryContractSuccess,
        loadTrialGroupLaboratoryContract,
        trialGroupLaboratoryContractIdNumber,
        navigate,
        saveAndReturn,
        clearTrialGroupLaboratoryContract,
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroupLaboratoryIdNumber,
        trialGroupLaboratoryContract,
    ]);

    // Failed saving
    useEffect(() => {
        if (
            !savingTrialGroupLaboratoryContract &&
            saveTrialGroupLaboratoryContractFailure
        ) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [
        savingTrialGroupLaboratoryContract,
        saveTrialGroupLaboratoryContractSuccess,
        loadTrialGroupLaboratoryContract,
        trialGroupLaboratoryContractIdNumber,
    ]);

    // Cleanup effect to clear the trial group laboratory & contract on component unmount
    useEffect(() => {
        return () => {
            clearTrialGroupLaboratoryContract();
            clearTrialGroupLaboratory();
            resetForm(reduxFormName);
        };
    }, [
        clearTrialGroupLaboratoryContract,
        clearTrialGroupLaboratory,
        resetForm,
        reduxFormName,
    ]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialGroupLaboratoryContract = {} as Dtos.TrialGroupLaboratoryContract;

            if (!data.trialGroupLaboratoryId) {
                changes.trialGroupLaboratoryId = trialGroupLaboratoryIdNumber;
            }

            await saveTrialGroupLaboratoryContract(
                Object.assign({ ...data }, changes)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialGroupLaboratoryContract) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        const baseRoute = location?.pathname?.split("/")[1];
        const url =
            baseRoute === "trials"
                ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryIdNumber}/contracts`
                : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryIdNumber}/contracts`;

        navigate(url);
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialGroupLaboratoryContract = {} as Dtos.TrialGroupLaboratoryContract;

            if (
                form &&
                hasBeenSubmitted &&
                !validatingTrialGroupLaboratoryContract
            ) {
                await validateTrialGroupLaboratoryContract(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const openLink = (link: string) => {
        window.open(`${link}`, "_blank");
        return null;
    };

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                undefined,
                trialGroupLaboratoryContractIdNumber,
                permissions,
                Dtos.Permission.TrialGroupLaboratoryContractUpdate,
                Dtos.Permission.TrialGroupLaboratoryContractCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialGroupLaboratoryContract ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratoryContract",
                                        {
                                            name: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName: "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratoryContract",
                                        {
                                            location: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    addOnRightButton: (
                                                        <button
                                                            className="btn btn-secondary"
                                                            type="button"
                                                            id="button-addon2"
                                                            disabled={form?.location == null || !isUrl(form?.location)}
                                                            style={{
                                                                cursor: form?.location == null || !isUrl(form?.location)
                                                                        ? "not-allowed"
                                                                        : "pointer",
                                                                padding: "4px 10px",
                                                            }}
                                                            onClick={() => {
                                                                openLink(form.location);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ARROWUPRIGHTFROMSQUARE} />
                                                        </button>
                                                    ),
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName: "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratoryContract",
                                        {
                                            dateSentToSite: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName: "col-sm-12 col-xl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratoryContract",
                                        {
                                            dateConfirmedBySite: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName: "col-sm-12 col-xl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Details</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        {/* {(AuthenticationHelper.hasPermission(permissions, Dtos.Permission.LabView) && trialGroupLaboratoryContractIdNumber) ? (
                            <Link 
                                className={"btn btn-primary"} 
                                url={`/labs/${trialGroupLaboratoryContract?.laboratoryId}/details`}
                                target='_blank'
                            >
                                {`Primary  `}
                                <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ARROWUPRIGHTFROMSQUARE} />
                            </Link>
                        ) : null} */}
                    </div>
                </div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "trialGroupLaboratoryContract",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(reduxFormName + ".saveAndReturn", false);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(reduxFormName + ".saveAndReturn", true);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </>
        );
    }, [
        AuthenticationHelper,
        trialGroupLaboratoryContractIdNumber,
        permissions,
        savingTrialGroupLaboratoryContract,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        trialGroupLaboratory,
        trialGroupLaboratoryContract,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialGroupLaboratoryContractView}
            loading={
                loadingTrialGroupLaboratoryContract ||
                creatingTrialGroupLaboratoryContract ||
                savingTrialGroupLaboratoryContract
            }
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialGroupLaboratoryContractPageProps> => {
    let trialGroupLaboratoryContract:
        | Dtos.TrialGroupLaboratoryContract
        | undefined = !(
        state.trialGroupLaboratoryContract.formData instanceof Array
    )
        ? state.trialGroupLaboratoryContract.formData
        : undefined;
    let trialGroupLaboratory: Dtos.TrialGroupLaboratory | undefined = !(
        state.trialGroupLaboratory.formData instanceof Array
    )
        ? state.trialGroupLaboratory.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialGroupLaboratoryContract: trialGroupLaboratoryContract!,
        loadingTrialGroupLaboratoryContract: state.trialGroupLaboratoryContract.loadState && state.trialGroupLaboratoryContract.loadState.status == RequestState.Pending,
        loadTrialGroupLaboratoryContractSuccess: state.trialGroupLaboratoryContract.loadState && state.trialGroupLaboratoryContract.loadState.status == RequestState.Success,
        loadTrialGroupLaboratoryContractFailure: state.trialGroupLaboratoryContract.loadState && state.trialGroupLaboratoryContract.loadState.status == RequestState.Failure,
        loadTrialGroupLaboratoryContractErrorCode: state.trialGroupLaboratoryContract.loadState.errorCode,
        savingTrialGroupLaboratoryContract: state.trialGroupLaboratoryContract.saveState && state.trialGroupLaboratoryContract.saveState.status == RequestState.Pending,
        saveTrialGroupLaboratoryContractSuccess: state.trialGroupLaboratoryContract.saveState && state.trialGroupLaboratoryContract.saveState.status == RequestState.Success,
        saveTrialGroupLaboratoryContractFailure: state.trialGroupLaboratoryContract.saveState && state.trialGroupLaboratoryContract.saveState.status == RequestState.Failure,
        creatingTrialGroupLaboratoryContract: state.trialGroupLaboratoryContract.createState && state.trialGroupLaboratoryContract.createState.status == RequestState.Pending,
        createTrialGroupLaboratoryContractSuccess: state.trialGroupLaboratoryContract.createState && state.trialGroupLaboratoryContract.createState.status == RequestState.Success,
        createTrialGroupLaboratoryContractFailure: state.trialGroupLaboratoryContract.createState && state.trialGroupLaboratoryContract.createState.status == RequestState.Failure,
        validatingTrialGroupLaboratoryContract: state.trialGroupLaboratoryContract.validateState && state.trialGroupLaboratoryContract.validateState.status == RequestState.Pending,
        user: state.user.data,
        permissions: permissions,
        lookups: state.trialGroupLaboratoryContract.lookups,
        form: state.reduxForms.trialGroupLaboratoryContract,
        formState: state.trialGroupLaboratoryContract.formState,
        formProperties: state.trialGroupLaboratoryContract.formProperties,
        reduxFormState: state.reduxForms.formState.trialGroupLaboratoryContract,
        validationFailures: trialGroupLaboratoryContract
            ? state.trialGroupLaboratoryContract.validationFailures
            : undefined,
        trialGroupLaboratory: trialGroupLaboratory!,
        loadingTrialGroupLaboratory: state.trialGroupLaboratory.loadState && state.trialGroupLaboratory.loadState.status == RequestState.Pending,
        loadTrialGroupLaboratorySuccess: state.trialGroupLaboratory.loadState && state.trialGroupLaboratory.loadState.status == RequestState.Success,
        loadTrialGroupLaboratoryFailure: state.trialGroupLaboratory.loadState && state.trialGroupLaboratory.loadState.status == RequestState.Failure,
        loadTrialGroupLaboratoryErrorCode: state.trialGroupLaboratory.loadState.errorCode,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialGroupLaboratoryContractPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialGroupLaboratoryContract: bindActionCreators(TrialGroupLaboratoryContractActions.LoadTrialGroupLaboratoryContractById, dispatch),
        clearTrialGroupLaboratoryContract: bindActionCreators(TrialGroupLaboratoryContractActions.Clear, dispatch),
        saveTrialGroupLaboratoryContract: bindActionCreators(TrialGroupLaboratoryContractActions.SaveTrialGroupLaboratoryContract, dispatch),
        createTrialGroupLaboratoryContract: bindActionCreators(TrialGroupLaboratoryContractActions.CreateTrialGroupLaboratoryContract, dispatch),
        validateTrialGroupLaboratoryContract: bindActionCreators(TrialGroupLaboratoryContractActions.ValidateTrialGroupLaboratoryContract, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        loadTrialGroupLaboratory: bindActionCreators(TrialGroupLaboratoryActions.LoadTrialGroupLaboratoryById,  dispatch),
        clearTrialGroupLaboratory: bindActionCreators(TrialGroupLaboratoryActions.Clear, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialGroupLaboratoryContractPage);
